import { useEffect, useMemo, useState } from 'react';
import { api, type FeedbackItem, type FeedbackStatus } from '../api/client';
import { useAuth } from '../auth/AuthContext';

const STATUSES: FeedbackStatus[] = ['new', 'reviewed', 'done'];
const FILTERS: Array<{ key: 'all' | FeedbackStatus; label: string }> = [
  { key: 'all', label: 'All' },
  { key: 'new', label: 'New' },
  { key: 'reviewed', label: 'Reviewed' },
  { key: 'done', label: 'Done' },
];

export function FeedbackAdminPage() {
  const { user } = useAuth();
  const [items, setItems] = useState<FeedbackItem[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<'all' | FeedbackStatus>('all');
  const [busyId, setBusyId] = useState<string | null>(null);

  useEffect(() => {
    if (!user?.isAdmin) return;
    let cancelled = false;
    api
      .listFeedback()
      .then((list) => !cancelled && setItems(list))
      .catch((e) => !cancelled && setError(e instanceof Error ? e.message : 'Failed to load.'));
    return () => {
      cancelled = true;
    };
  }, [user?.isAdmin]);

  const counts = useMemo(() => {
    const c = { all: items?.length ?? 0, new: 0, reviewed: 0, done: 0 };
    for (const it of items ?? []) c[it.status]++;
    return c;
  }, [items]);

  const shown = (items ?? []).filter((it) => filter === 'all' || it.status === filter);

  if (!user?.isAdmin) {
    return (
      <div className="empty">
        <p>You need to be an admin to view feedback requests.</p>
        <a className="btn" href="#/">
          ← Home
        </a>
      </div>
    );
  }

  const setStatus = async (it: FeedbackItem, status: FeedbackStatus) => {
    if (it.status === status) return;
    setBusyId(it.id);
    try {
      await api.setFeedbackStatus(it.id, it.createdAt, status);
      setItems((prev) => prev?.map((x) => (x.id === it.id ? { ...x, status } : x)) ?? prev);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Update failed.');
    } finally {
      setBusyId(null);
    }
  };

  const remove = async (it: FeedbackItem) => {
    if (!confirm(`Delete this request?\n\n"${it.summary}"`)) return;
    setBusyId(it.id);
    try {
      await api.deleteFeedback(it.id, it.createdAt);
      setItems((prev) => prev?.filter((x) => x.id !== it.id) ?? prev);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Delete failed.');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <>
      <div className="page-head">
        <h1 className="page-title">Feedback requests</h1>
        <a className="back" href="#/">
          ← Home
        </a>
      </div>

      <div className="fb-filters">
        {FILTERS.map((f) => (
          <button
            key={f.key}
            type="button"
            className={`chip${filter === f.key ? ' on' : ''}`}
            onClick={() => setFilter(f.key)}
          >
            {f.label} ({counts[f.key]})
          </button>
        ))}
      </div>

      {error && <p className="auth-error">{error}</p>}

      {items === null ? (
        <p className="empty small">Loading…</p>
      ) : shown.length === 0 ? (
        <p className="empty small">
          {counts.all === 0 ? 'No requests yet.' : 'Nothing in this view.'}
        </p>
      ) : (
        <div className="fb-list">
          {shown.map((it) => (
            <div className={`fb-row status-${it.status}`} key={it.id}>
              <div className="fb-row-head">
                <span className={`fb-imp imp-${it.importance.replace(/\s|-/g, '').toLowerCase()}`}>
                  {it.importance}
                </span>
                <h2 className="fb-summary">{it.summary}</h2>
                <span className={`fb-status-pill s-${it.status}`}>{it.status}</span>
              </div>

              {it.details && <p className="fb-row-details">{it.details}</p>}

              <div className="fb-meta">
                {it.context && <span>{it.context}</span>}
                {it.submitterEmail && (
                  <span>
                    from <a href={`mailto:${it.submitterEmail}`}>{it.submitterEmail}</a>
                  </span>
                )}
                <span>{new Date(it.createdAt).toLocaleString()}</span>
              </div>

              {it.imageUrls.length > 0 && (
                <div className="fb-thumbs">
                  {it.imageUrls.map((url, i) => (
                    <a className="fb-thumb" href={url} target="_blank" rel="noreferrer" key={i}>
                      <img src={url} alt={`Attachment ${i + 1}`} />
                    </a>
                  ))}
                </div>
              )}

              <div className="fb-row-actions">
                <div className="fb-status-set">
                  {STATUSES.map((s) => (
                    <button
                      key={s}
                      type="button"
                      className={`chip${it.status === s ? ' on' : ''}`}
                      disabled={busyId === it.id}
                      onClick={() => setStatus(it, s)}
                    >
                      {s}
                    </button>
                  ))}
                </div>
                <button
                  type="button"
                  className="btn danger sm"
                  disabled={busyId === it.id}
                  onClick={() => remove(it)}
                >
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </>
  );
}
